import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  confirmIndustrialCostProductsImport,
  createIndustrialCostProduct,
  deleteIndustrialCostProduct,
  getIndustrialCostProducts,
  previewIndustrialCostProductsImport,
  updateIndustrialCostProduct,
} from '../services/industrialCostService';
import { analyzeFileNormalization, commitFileNormalization } from '../fileNormalization/service';
import IndustrialCostSectionPlaceholder from '../components/IndustrialCostSectionPlaceholder';
import type {
  CreateProductPayload,
  Product,
  ProductImportPreviewResponse,
} from '../types/industrialCost';

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const maybeResponse = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeResponse.response?.data?.detail) {
      return maybeResponse.response.data.detail;
    }

    if (maybeResponse.message) {
      return maybeResponse.message;
    }
  }

  return 'Se produjo un error inesperado.';
}

const initialForm: CreateProductPayload = {
  name: '',
  code: '',
  family: '',
  format: '',
  unit_label: '',
  is_active: true,
  description: '',
};

const productImportFields = [
  { key: 'code', label: 'Código / ID', required: true },
  { key: 'name', label: 'Nombre', required: false },
  { key: 'family', label: 'Familia', required: false },
  { key: 'format', label: 'Formato', required: false },
  { key: 'unit_label', label: 'Unidad', required: false },
  { key: 'description', label: 'Descripción', required: false },
  { key: 'is_active', label: 'Activo', required: false },
] as const;

export default function IndustrialCostProductsView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState<CreateProductPayload>(initialForm);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<Product | null>(null);
  const [editForm, setEditForm] = useState<CreateProductPayload>(initialForm);
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<ProductImportPreviewResponse | null>(null);
  const [importMapping, setImportMapping] = useState<Record<string, string | null>>({});
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [normalizationSheetName, setNormalizationSheetName] = useState<string | null>(null);
  const [normalizationHeaderRowIndex, setNormalizationHeaderRowIndex] = useState<number | null>(null);
  const [productSearchText, setProductSearchText] = useState('');

  const {
    data: products = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['industrial-cost-products'],
    queryFn: getIndustrialCostProducts,
  });

  const createMutation = useMutation({
    mutationFn: createIndustrialCostProduct,
    onSuccess: async () => {
      enqueueSnackbar('Producto creado correctamente.', { variant: 'success' });
      setForm(initialForm);
      await queryClient.invalidateQueries({ queryKey: ['industrial-cost-products'] });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ productId, payload }: { productId: string; payload: CreateProductPayload }) =>
      updateIndustrialCostProduct(productId, payload),
    onSuccess: async () => {
      enqueueSnackbar('Producto actualizado correctamente.', { variant: 'success' });
      setEditingProduct(null);
      await queryClient.invalidateQueries({ queryKey: ['industrial-cost-products'] });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (productId: string) => deleteIndustrialCostProduct(productId),
    onSuccess: async () => {
      enqueueSnackbar('Producto eliminado correctamente.', { variant: 'success' });
      setDeletingProduct(null);
      await queryClient.invalidateQueries({ queryKey: ['industrial-cost-products'] });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const previewImportMutation = useMutation({
    mutationFn: ({
      file,
      mapping,
      sheetName,
      headerRowIndex,
    }: {
      file: File;
      mapping?: Record<string, string | null>;
      sheetName?: string | null;
      headerRowIndex?: number | null;
    }) =>
      previewIndustrialCostProductsImport(file, mapping, sheetName ?? null, headerRowIndex ?? null),
    onSuccess: (preview) => {
      setImportPreview(preview);
      setImportMapping(preview.auto_mapping);
      setImportDialogOpen(true);
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const confirmImportMutation = useMutation({
    mutationFn: ({
      file,
      mapping,
      sheetName,
      headerRowIndex,
    }: {
      file: File;
      mapping: Record<string, string | null>;
      sheetName?: string | null;
      headerRowIndex?: number | null;
    }) =>
      confirmIndustrialCostProductsImport(file, mapping, sheetName ?? null, headerRowIndex ?? null),
    onSuccess: async (result) => {
      try {
        await commitFileNormalization({
          importer_key: 'products',
          sheet_name: normalizationSheetName,
          header_row_index: normalizationHeaderRowIndex ?? 0,
          data_start_row_index: (normalizationHeaderRowIndex ?? 0) + 1,
          column_mapping: Object.fromEntries(
            Object.entries(importMapping).filter(([, v]) => v !== null) as [string, string][],
          ),
          known_columns: Object.values(importMapping).filter((v): v is string => v !== null),
        });
      } catch {
        // El commit de normalización no es bloqueante
      }
      enqueueSnackbar(
        `Importación completada: ${result.rows_created} creados, ${result.rows_updated} actualizados.`,
        { variant: 'success' },
      );
      setImportDialogOpen(false);
      setImportFile(null);
      setImportPreview(null);
      setNormalizationSheetName(null);
      setNormalizationHeaderRowIndex(null);
      if (importInputRef.current) {
        importInputRef.current.value = '';
      }
      await queryClient.invalidateQueries({ queryKey: ['industrial-cost-products'] });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const counts = useMemo(() => {
    const active = products.filter((item) => item.is_active).length;
    const withFamily = products.filter((item) => !!item.family).length;
    const withFormat = products.filter((item) => !!item.format).length;

    return {
      total: products.length,
      active,
      withFamily,
      withFormat,
    };
  }, [products]);

  const filteredProducts = useMemo(() => {
    const normalizedSearch = productSearchText.trim().toLowerCase();

    if (!normalizedSearch) {
      return products;
    }

    return products.filter((product) => {
      const statusLabel = product.is_active ? 'activo' : 'inactivo';

      const searchableText = [
        product.name,
        product.code,
        product.family,
        product.format,
        product.unit_label,
        product.description,
        statusLabel,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(normalizedSearch);
    });
  }, [products, productSearchText]);

  const PRODUCT_TABLE_LIMIT = 25;

  const visibleProducts = useMemo(
    () => filteredProducts.slice(0, PRODUCT_TABLE_LIMIT),
    [filteredProducts],
  );

  const handleChange = <K extends keyof CreateProductPayload>(
    key: K,
    value: CreateProductPayload[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleEditChange = <K extends keyof CreateProductPayload>(
    key: K,
    value: CreateProductPayload[K],
  ) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    createMutation.mutate({
      ...form,
      name: form.name.trim(),
      code: form.code.trim(),
      family: form.family?.trim() || null,
      format: form.format?.trim() || null,
      unit_label: form.unit_label?.trim() || null,
      description: form.description?.trim() || null,
    });
  };

  const handleOpenEdit = (product: Product) => {
    setEditingProduct(product);
    setEditForm({
      name: product.name,
      code: product.code,
      family: product.family || '',
      format: product.format || '',
      unit_label: product.unit_label || '',
      is_active: product.is_active,
      description: product.description || '',
    });
  };

  const handleConfirmEdit = () => {
    if (!editingProduct) return;

    updateMutation.mutate({
      productId: editingProduct.id,
      payload: {
        ...editForm,
        name: editForm.name.trim(),
        code: editForm.code.trim(),
        family: editForm.family?.trim() || null,
        format: editForm.format?.trim() || null,
        unit_label: editForm.unit_label?.trim() || null,
        description: editForm.description?.trim() || null,
      },
    });
  };

  const handleConfirmDelete = () => {
    if (!deletingProduct) return;
    deleteMutation.mutate(deletingProduct.id);
  };

  const handleSelectImportFile = async (file: File | null) => {
    if (!file) return;

    setImportFile(file);
    setImportPreview(null);
    setImportMapping({});
    setNormalizationSheetName(null);
    setNormalizationHeaderRowIndex(null);

    try {
      const normalization = await analyzeFileNormalization(file, 'products');
      const resolvedSheetName = normalization.selected_sheet ?? null;
      const resolvedHeaderRowIndex = normalization.header_row_index ?? null;
      setNormalizationSheetName(resolvedSheetName);
      setNormalizationHeaderRowIndex(resolvedHeaderRowIndex);
      previewImportMutation.mutate({ file, sheetName: resolvedSheetName, headerRowIndex: resolvedHeaderRowIndex });
    } catch {
      previewImportMutation.mutate({ file });
    }
  };

  const handleConfirmImport = () => {
    if (!importFile || !importPreview) return;

    confirmImportMutation.mutate({
      file: importFile,
      mapping: importMapping,
      sheetName: normalizationSheetName,
      headerRowIndex: normalizationHeaderRowIndex,
    });
  };

  const handleChangeImportMapping = (field: string, column: string) => {
    if (!importFile) return;

    const nextMapping = {
      ...importMapping,
      [field]: column || null,
    };

    setImportMapping(nextMapping);
    previewImportMutation.mutate({
      file: importFile,
      mapping: nextMapping,
    });
  };

  const handleCloseImportDialog = () => {
    if (confirmImportMutation.isPending) return;

    setImportDialogOpen(false);
    setImportFile(null);
    setImportPreview(null);
    setImportMapping({});

    if (importInputRef.current) {
      importInputRef.current.value = '';
    }
  };

  const canSubmit = form.name.trim().length > 0 && form.code.trim().length > 0;

  const canEditSubmit =
    editForm.name.trim().length > 0 && editForm.code.trim().length > 0;

  return (
    <>
      <Stack spacing={3}>
        <input
          ref={importInputRef}
          type="file"
          accept=".csv,.xlsx"
          hidden
          onChange={(event) => handleSelectImportFile(event.target.files?.[0] ?? null)}
        />
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Stack spacing={1.25}>
            <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
              Productos
            </Typography>

            <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.7 }}>
              En v1 el producto es el objeto final imputable sobre el que se agregan
              costes directos e indirectos.
            </Typography>

            <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
              No consta cerrada para siempre la definición final del objeto imputable,
              pero operativamente trabajamos con products como base del cálculo.
            </Typography>
          </Stack>
        </Paper>

        <Grid container spacing={2.5}>
          <Grid item xs={12} xl={4}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 4,
                border: '1px solid rgba(148,163,184,0.18)',
                bgcolor: 'rgba(15,23,42,0.66)',
                backdropFilter: 'blur(10px)',
                px: 3,
                py: 3,
              }}
            >
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                  Nuevo producto
                </Typography>

                <TextField
                  label="Nombre"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  fullWidth
                  size="small"
                />

                <TextField
                  label="Código"
                  value={form.code}
                  onChange={(e) => handleChange('code', e.target.value)}
                  fullWidth
                  size="small"
                />

                <TextField
                  label="Familia"
                  value={form.family ?? ''}
                  onChange={(e) => handleChange('family', e.target.value)}
                  fullWidth
                  size="small"
                />

                <TextField
                  label="Formato"
                  value={form.format ?? ''}
                  onChange={(e) => handleChange('format', e.target.value)}
                  fullWidth
                  size="small"
                />

                <TextField
                  label="Unidad final principal"
                  value={form.unit_label ?? ''}
                  onChange={(e) => handleChange('unit_label', e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="kg, units, latas..."
                />

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 3,
                    bgcolor: 'rgba(30,41,59,0.45)',
                    border: '1px solid rgba(148,163,184,0.14)',
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 700 }}>
                      Activo
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      El producto queda disponible para trabajo operativo.
                    </Typography>
                  </Box>

                  <Switch
                    checked={form.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                  />
                </Stack>

                <TextField
                  label="Descripción"
                  value={form.description ?? ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  minRows={3}
                />

                <Button
                  variant="contained"
                  size="large"
                  disabled={!canSubmit || createMutation.isPending}
                  onClick={handleSubmit}
                  sx={{
                    mt: 1,
                    py: 1.35,
                    fontWeight: 800,
                    bgcolor: '#38bdf8',
                    color: '#0f172a',
                    '&:hover': {
                      bgcolor: '#38bdf8',
                      filter: 'brightness(1.06)',
                    },
                  }}
                >
                  {createMutation.isPending ? 'Guardando...' : 'Crear producto'}
                </Button>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} xl={8}>
            <Stack spacing={2.5}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr 1fr',
                    md: 'repeat(4, minmax(0, 1fr))',
                  },
                  gap: 1.5,
                }}
              >
                {[
                  { label: 'Total', value: counts.total },
                  { label: 'Activos', value: counts.active },
                  { label: 'Con familia', value: counts.withFamily },
                  { label: 'Con formato', value: counts.withFormat },
                ].map((item) => (
                  <Paper
                    key={item.label}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid rgba(148,163,184,0.16)',
                      bgcolor: 'rgba(15,23,42,0.58)',
                      px: 2,
                      py: 1.75,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      {item.label}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{ color: '#f8fafc', fontWeight: 900, mt: 0.4 }}
                    >
                      {item.value}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: '1px solid rgba(148,163,184,0.18)',
                  bgcolor: 'rgba(15,23,42,0.66)',
                  backdropFilter: 'blur(10px)',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                        Catálogo operativo
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                        Productos configurados para el cálculo de costes.
                      </Typography>
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <TextField
                        label="Filtrar productos"
                        value={productSearchText}
                        onChange={(event) => setProductSearchText(event.target.value)}
                        size="small"
                        placeholder="Código, nombre, familia, formato..."
                        sx={{ minWidth: { xs: '100%', sm: 280 } }}
                      />

                      <Button
                        variant="outlined"
                        disabled={previewImportMutation.isPending}
                        onClick={() => importInputRef.current?.click()}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 800,
                          borderColor: 'rgba(56,189,248,0.34)',
                          color: '#38bdf8',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {previewImportMutation.isPending ? 'Leyendo archivo...' : 'Importar productos'}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>

                {isError ? (
                  <Box sx={{ p: 3 }}>
                    <Alert severity="error">{getErrorMessage(error)}</Alert>
                  </Box>
                ) : isLoading ? (
                  <Box sx={{ p: 3 }}>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      Cargando productos...
                    </Typography>
                  </Box>
                ) : products.length === 0 ? (
                  <Box sx={{ p: 3 }}>
                    <IndustrialCostSectionPlaceholder
                      title="Sin productos todavía"
                      description="Crea los primeros productos para disponer del objeto final sobre el que se imputará el coste."
                      note="La definición exacta del objeto final podrá refinarse más adelante, pero v1 opera con products."
                    />
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        px: 3,
                        py: 1.5,
                        borderBottom: '1px solid rgba(148,163,184,0.1)',
                        bgcolor: 'rgba(2,6,23,0.18)',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        Mostrando {visibleProducts.length} de {filteredProducts.length} productos filtrados
                        {filteredProducts.length !== products.length
                          ? ` · ${products.length} productos totales`
                          : ''}
                        {filteredProducts.length > visibleProducts.length
                          ? ` · se muestran solo los primeros ${PRODUCT_TABLE_LIMIT}; afina el filtro para localizar productos concretos`
                          : ''}
                      </Typography>
                    </Box>

                    {filteredProducts.length === 0 ? (
                      <Box sx={{ p: 3 }}>
                        <Alert severity="info">
                          No hay productos que coincidan con el filtro aplicado.
                        </Alert>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          maxHeight: 680,
                          overflow: 'auto',
                        }}
                      >
                        <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Nombre</TableCell>
                          <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Código</TableCell>
                          <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Familia</TableCell>
                          <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Formato</TableCell>
                          <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Unidad</TableCell>
                          <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Estado</TableCell>
                          <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Descripción</TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: '#94a3b8', fontWeight: 800, minWidth: 180 }}
                          >
                            Acciones
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {visibleProducts.map((product) => (
                          <TableRow key={product.id} hover>
                            <TableCell sx={{ color: '#f8fafc', fontWeight: 700 }}>
                              {product.name}
                            </TableCell>
                            <TableCell sx={{ color: '#cbd5e1' }}>{product.code}</TableCell>
                            <TableCell sx={{ color: '#cbd5e1' }}>
                              {product.family || '—'}
                            </TableCell>
                            <TableCell sx={{ color: '#cbd5e1' }}>
                              {product.format || '—'}
                            </TableCell>
                            <TableCell sx={{ color: '#cbd5e1' }}>
                              {product.unit_label || '—'}
                            </TableCell>
                            <TableCell sx={{ color: '#cbd5e1' }}>
                              <Chip
                                label={product.is_active ? 'Activo' : 'Inactivo'}
                                size="small"
                                sx={{
                                  bgcolor: product.is_active
                                    ? 'rgba(34,197,94,0.10)'
                                    : 'rgba(148,163,184,0.10)',
                                  color: product.is_active ? '#22c55e' : '#cbd5e1',
                                  border: `1px solid ${
                                    product.is_active
                                      ? 'rgba(34,197,94,0.24)'
                                      : 'rgba(148,163,184,0.22)'
                                  }`,
                                  fontWeight: 800,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: '#94a3b8' }}>
                              {product.description || '—'}
                            </TableCell>
                            <TableCell align="right">
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="flex-end"
                                flexWrap="wrap"
                              >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleOpenEdit(product)}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderColor: 'rgba(56,189,248,0.28)',
                                    color: '#38bdf8',
                                  }}
                                >
                                  Editar
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => setDeletingProduct(product)}
                                  sx={{ textTransform: 'none', fontWeight: 700 }}
                                >
                                  Eliminar
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                    )}
                  </>
                )}
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Stack>

      <Dialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Importar productos</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              La importación usa el código como clave. Si el código ya existe, el producto se actualiza; si no existe, se crea.
            </Alert>

            {importPreview ? (
              <>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Filas del archivo</Typography>
                    <Typography variant="h6">{importPreview.coverage_summary.total_rows}</Typography>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Códigos únicos</Typography>
                    <Typography variant="h6">{importPreview.coverage_summary.unique_codes_count}</Typography>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Nuevos</Typography>
                    <Typography variant="h6">
                      {importPreview.coverage_summary.new_codes_count}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                        ({importPreview.coverage_summary.new_codes_pct}%)
                      </Typography>
                    </Typography>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Actualizarán existentes</Typography>
                    <Typography variant="h6">
                      {importPreview.coverage_summary.existing_codes_count}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                        ({importPreview.coverage_summary.existing_codes_pct}%)
                      </Typography>
                    </Typography>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Sin código</Typography>
                    <Typography variant="h6">{importPreview.coverage_summary.rows_without_code}</Typography>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Sin nombre</Typography>
                    <Typography variant="h6">{importPreview.coverage_summary.rows_without_name}</Typography>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Códigos duplicados</Typography>
                    <Typography variant="h6">{importPreview.coverage_summary.duplicate_codes_count}</Typography>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Filas duplicadas ignoradas</Typography>
                    <Typography variant="h6">
                      {importPreview.coverage_summary.duplicate_rows_count}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                        ({importPreview.coverage_summary.duplicate_rows_pct}%)
                      </Typography>
                    </Typography>
                  </Grid>
                </Grid>

                <Alert severity="info">
                  Regla de importación: el código identifica el producto. Si el código ya existe en la base de datos, se actualizará. Si el archivo contiene el mismo código varias veces, se usará la primera aparición y las siguientes se ignorarán.
                </Alert>

                {importPreview.coverage_summary.duplicate_codes_sample.length > 0 ? (
                  <Alert severity="warning">
                    Códigos duplicados detectados en el archivo:{' '}
                    {importPreview.coverage_summary.duplicate_codes_sample.join(', ')}
                    {importPreview.coverage_summary.duplicate_codes_count >
                    importPreview.coverage_summary.duplicate_codes_sample.length
                      ? '…'
                      : ''}
                  </Alert>
                ) : null}

                {importPreview.missing_required_fields.length > 0 ? (
                  <Alert severity="warning">
                    Faltan campos obligatorios en el mapeo: {importPreview.missing_required_fields.join(', ')}.
                  </Alert>
                ) : null}

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                    Mapeo de columnas
                  </Typography>

                  <Grid container spacing={1.5}>
                    {productImportFields.map((field) => (
                      <Grid item xs={12} sm={6} md={4} key={field.key}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label={`${field.label}${field.required ? ' *' : ''}`}
                          value={importMapping[field.key] ?? ''}
                          onChange={(event) =>
                            handleChangeImportMapping(field.key, event.target.value)
                          }
                        >
                          <MenuItem value="">
                            <em>Sin mapear</em>
                          </MenuItem>

                          {importPreview.columns.map((column) => (
                            <MenuItem key={column} value={column}>
                              {column}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Código</TableCell>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Familia</TableCell>
                        <TableCell>Formato</TableCell>
                        <TableCell>Unidad</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importPreview.preview_rows.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{String(row.code ?? '—')}</TableCell>
                          <TableCell>{String(row.name ?? '—')}</TableCell>
                          <TableCell>{String(row.family ?? '—')}</TableCell>
                          <TableCell>{String(row.format ?? '—')}</TableCell>
                          <TableCell>{String(row.unit_label ?? '—')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </>
            ) : null}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={handleCloseImportDialog}
            disabled={confirmImportMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmImport}
            disabled={
              !importPreview ||
              importPreview.missing_required_fields.length > 0 ||
              confirmImportMutation.isPending
            }
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#38bdf8',
              color: '#0f172a',
              '&:hover': { bgcolor: '#38bdf8', filter: 'brightness(1.06)' },
            }}
          >
            {confirmImportMutation.isPending ? 'Importando...' : 'Confirmar importación'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!editingProduct}
        onClose={() => !updateMutation.isPending && setEditingProduct(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar producto</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre"
              value={editForm.name}
              onChange={(e) => handleEditChange('name', e.target.value)}
              fullWidth
              size="small"
            />

            <TextField
              label="Código"
              value={editForm.code}
              onChange={(e) => handleEditChange('code', e.target.value)}
              fullWidth
              size="small"
            />

            <TextField
              label="Familia"
              value={editForm.family ?? ''}
              onChange={(e) => handleEditChange('family', e.target.value)}
              fullWidth
              size="small"
            />

            <TextField
              label="Formato"
              value={editForm.format ?? ''}
              onChange={(e) => handleEditChange('format', e.target.value)}
              fullWidth
              size="small"
            />

            <TextField
              label="Unidad final principal"
              value={editForm.unit_label ?? ''}
              onChange={(e) => handleEditChange('unit_label', e.target.value)}
              fullWidth
              size="small"
            />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 3,
                bgcolor: 'rgba(30,41,59,0.06)',
                border: '1px solid rgba(148,163,184,0.18)',
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Activo
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Cambia su disponibilidad operativa.
                </Typography>
              </Box>

              <Switch
                checked={editForm.is_active}
                onChange={(e) => handleEditChange('is_active', e.target.checked)}
              />
            </Stack>

            <TextField
              label="Descripción"
              value={editForm.description ?? ''}
              onChange={(e) => handleEditChange('description', e.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setEditingProduct(null)}
            disabled={updateMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmEdit}
            disabled={!canEditSubmit || updateMutation.isPending}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#38bdf8',
              color: '#0f172a',
              '&:hover': { bgcolor: '#38bdf8', filter: 'brightness(1.06)' },
            }}
          >
            {updateMutation.isPending ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deletingProduct}
        onClose={() => !deleteMutation.isPending && setDeletingProduct(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Eliminar producto</DialogTitle>

        <DialogContent>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            {deletingProduct
              ? `Vas a eliminar el producto "${deletingProduct.name}". Esta acción no se puede deshacer.`
              : ''}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDeletingProduct(null)}
            disabled={deleteMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}